<template>
  <q-card v-if="organization" flat bordered>
    <q-card-section class="flex justify-between">
      <div>
        <div class="text-h6">Organization Logo</div>
        <div style="font-size: 12px">
          Upload a logo that will appear at the top of your PDF exports.
        </div>
      </div>
      <div class="q-my-auto">
        <q-btn
          v-if="organization.logo_url"
          no-caps
          flat
          label="Remove Logo"
          @click="removeOrganizationLogo"
          class="q-mr-sm"
        />
        <q-btn
          no-caps
          flat
          :label="organization.logo_url ? 'Update Logo' : 'Upload Logo'"
          @click="showLogoUploadDialog = true"
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <q-img
        v-if="organization.logo_url"
        :src="organization.logo_url"
        referrerpolicy="no-referrer"
        no-spinner
        fit="fill"
        width="150px"
      />
      <div v-else class="row items-center">
        <q-icon name="info_outline" size="sm" class="q-mr-md" />
        <div>Add your company's logo to brand and personalize your PDF exports.</div>
      </div>
    </q-card-section>
  </q-card>
  <q-dialog v-model="showLogoUploadDialog">
    <q-card style="width: 500px; max-width: 90vw">
      <q-card-section>
        <div class="text-h6">Upload Logo</div>
        <div style="font-size: 12px">Pick a logo to upload for your organization.</div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <q-file
          v-model="organizationLogo"
          filled
          accept=".jpg,.svg"
          max-total-size="500000"
          label="Upload a new logo"
          counter
          @rejected="onRejectedFile"
        >
          <template #prepend>
            <q-icon name="cloud_upload" @click.stop.prevent />
          </template>
          <template #append>
            <q-icon
              name="close"
              class="cursor-pointer"
              @click.stop.prevent="organizationLogo = null"
            />
          </template>
          <template #hint> Accepted file types: .jpg, .svg (max 500kb) </template>
        </q-file>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn no-caps flat label="Cancel" @click="showLogoUploadDialog = false" />
        <q-btn
          no-caps
          unelevated
          color="primary"
          label="Upload"
          :loading="logoUploading"
          :disable="logoUploading || !organizationLogo"
          @click="updateOrganizationLogo"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'

export default {
  name: 'OrganizationLogo',
  data() {
    return {
      organizationLogo: null as File | FileList | null | undefined,
      showLogoUploadDialog: false,
      logoUploading: false
    }
  },
  computed: {
    organization() {
      return useUserStore().organization
    }
  },
  watch: {
    organization() {
      this.organizationLogo = null
    }
  },
  methods: {
    removeOrganizationLogo() {
      this.$q
        .dialog({
          title: 'Remove Logo',
          message: 'Are you sure you want to remove the logo?',
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          if (this.organization?.id) {
            useUserStore()
              .updateOrganization(this.organization.id, {
                display_name: this.organization.display_name,
                owner_user_id: this.organization.owner_user_id,
                admin_user_ids: this.organization.admin_user_ids,
                connection_id: this.organization.connection_id,
                logo_url: null,
                report_tags: this.organization.report_tags,
                use_security_groups: this.organization.use_security_groups,
                sections: this.organization.sections,
                columns: this.organization.columns
              })
              .then(() => {
                this.organizationLogo = null
                this.$tracker.updatedLogo()
              })
          }
        })
    },
    onRejectedFile() {
      this.$q.notify({
        message: 'Invalid file type or size',
        color: 'blue',
        position: 'top'
      })
    },
    async updateOrganizationLogo() {
      let base64EncodedLogo: string | null = null

      switch (typeof this.organizationLogo) {
        case 'undefined':
          console.warn('Undefined not supported')
          break
        case 'string':
          console.warn('String not supported')
          break
        case 'object':
          if (this.organizationLogo instanceof FileList) {
            console.warn('FileList not supported')
          } else {
            const file = this.organizationLogo as File
            const reader = new FileReader()
            reader.readAsDataURL(file)
            base64EncodedLogo = await new Promise((resolve) => {
              reader.onload = () => {
                resolve(reader.result as string)
              }
            })
          }
          break
        default:
          console.warn('Unknown type for the organization logo')
          break
      }

      if (this.organization?.id) {
        useUserStore()
          .updateOrganization(this.organization.id, {
            display_name: this.organization.display_name,
            owner_user_id: this.organization.owner_user_id,
            admin_user_ids: this.organization.admin_user_ids,
            connection_id: this.organization.connection_id,
            logo_url: base64EncodedLogo,
            report_tags: this.organization.report_tags,
            use_security_groups: this.organization.use_security_groups,
            sections: this.organization.sections,
            columns: this.organization.columns
          })
          .then(() => {
            this.organizationLogo = null
            this.$tracker.updatedLogo()
            this.showLogoUploadDialog = false
          })
          .finally(() => {
            this.logoUploading = false
          })
      }
    }
  }
}
</script>
